import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { costingAPI } from '../../../api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell,
} from 'recharts';
import { Loader2, FileSpreadsheet, FileText, BarChart3, Layers } from 'lucide-react';
import { GREEN, RUST, MUTED, fmt2, downloadBlob } from './helpers';

const MAX_SELECT = 8;

// Distinct palette for stacked raw-material segments.
const RM_COLORS = [
  '#2B5339', '#C05C35', '#E89D4F', '#5C6B61', '#7B9E89', '#9C4722',
  '#B8863B', '#3E6B8A', '#8A5A8F', '#6B8E4E', '#C96A6A', '#4F7C8C',
  '#A67C52', '#557A46', '#8C6239', '#6E6E6E',
];

const METRICS = [
  { key: 'cost_per_kg', label: 'Cost / kg', money: true },
  { key: 'cost_per_bag', label: 'Cost / bag', money: true },
  { key: 'selling_price', label: 'Selling price / bag', money: true },
  { key: 'margin_abs', label: 'Margin ₹ / bag', money: true },
  { key: 'margin_pct', label: 'Margin %', money: false },
];

const shortName = (n) => (n && n.length > 16 ? `${n.slice(0, 15)}…` : n || '');

export const ComparisonSection = ({ refreshKey }) => {
  const [summaries, setSummaries] = useState([]);
  const [formulations, setFormulations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bagWeight, setBagWeight] = useState('50');
  const [selectedIds, setSelectedIds] = useState([]);
  const [metric, setMetric] = useState('cost_per_bag');
  const [rmMode, setRmMode] = useState('cost'); // 'cost' | 'percent'
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (bw) => {
    setLoading(true);
    try {
      const [sumRes, fRes] = await Promise.all([
        costingAPI.listCostSheets({ bag_weight_kg: parseFloat(bw) || 50 }),
        costingAPI.listFormulations(),
      ]);
      setSummaries(sumRes.data || []);
      setFormulations(fRes.data || []);
    } catch (e) {
      toast.error('Failed to load comparison data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(bagWeight); /* eslint-disable-next-line */ }, [refreshKey]);
  // Reload cost summaries when bag weight changes (cost/bag depends on it).
  useEffect(() => {
    const t = setTimeout(() => { load(bagWeight); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [bagWeight]);

  const formByPid = useMemo(() => {
    const m = {};
    formulations.forEach((f) => { m[f.product_id] = f; });
    return m;
  }, [formulations]);

  const toggle = (pid) => {
    setSelectedIds((prev) => {
      if (prev.includes(pid)) return prev.filter((p) => p !== pid);
      if (prev.length >= MAX_SELECT) {
        toast.error(`You can compare up to ${MAX_SELECT} products at once`);
        return prev;
      }
      return [...prev, pid];
    });
  };

  const selectAll = () => {
    const ids = summaries.map((s) => s.product_id).slice(0, MAX_SELECT);
    setSelectedIds(ids);
    if (summaries.length > MAX_SELECT) toast.info(`Showing first ${MAX_SELECT} of ${summaries.length} products`);
  };
  const clearAll = () => setSelectedIds([]);

  const selectedSummaries = useMemo(
    () => selectedIds.map((id) => summaries.find((s) => s.product_id === id)).filter(Boolean),
    [selectedIds, summaries],
  );

  const metricMeta = METRICS.find((m) => m.key === metric) || METRICS[1];

  // --- Cost comparison chart data ---
  const costChartData = useMemo(
    () => selectedSummaries.map((s) => ({
      name: shortName(s.product_name),
      fullName: s.product_name,
      value: Number(s[metric] ?? 0) || 0,
    })),
    [selectedSummaries, metric],
  );

  // --- RM mix chart data (stacked) ---
  const { rmChartData, rmKeys } = useMemo(() => {
    const keysOrder = [];
    const seen = new Set();
    const rows = selectedSummaries.map((s) => {
      const f = formByPid[s.product_id];
      const batch = Number(f?.batch_size_kg || 0);
      const row = { name: shortName(s.product_name), fullName: s.product_name };
      (f?.rows || []).forEach((r) => {
        const key = r.rm_name || '(unknown)';
        if (!seen.has(key)) { seen.add(key); keysOrder.push(key); }
        const val = rmMode === 'cost'
          ? (batch ? Number(r.cost_at_batch || 0) / batch : 0)
          : Number(r.percent || 0);
        row[key] = Number(val.toFixed(4));
      });
      return row;
    });
    return { rmChartData: rows, rmKeys: keysOrder };
  }, [selectedSummaries, formByPid, rmMode]);

  const doExport = async (kind) => {
    if (selectedIds.length === 0) { toast.error('Select at least one product'); return; }
    setExporting(true);
    try {
      const params = { product_ids: selectedIds.join(','), bag_weight_kg: parseFloat(bagWeight) || 50, rm_mode: rmMode };
      const res = kind === 'excel'
        ? await costingAPI.exportComparisonExcel(params)
        : await costingAPI.exportComparisonPdf(params);
      downloadBlob(res, `product_comparison.${kind === 'excel' ? 'xlsx' : 'pdf'}`);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const moneyFmt = (v) => `₹${fmt2(v)}`;
  const valueFmt = metricMeta.money ? moneyFmt : (v) => `${fmt2(v)}%`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="comparison-loading">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: GREEN }} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="comparison-section">
      {/* Controls */}
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-medium block mb-1" style={{ color: MUTED }}>Bag weight (kg)</label>
            <Input
              type="number"
              value={bagWeight}
              onChange={(e) => setBagWeight(e.target.value)}
              className="w-28 h-9"
              data-testid="comparison-bag-weight"
            />
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={selectAll} data-testid="comparison-select-all">Select all (max {MAX_SELECT})</Button>
            <Button size="sm" variant="outline" onClick={clearAll} data-testid="comparison-clear">Clear</Button>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button size="sm" onClick={() => doExport('excel')} disabled={exporting || selectedIds.length === 0} style={{ background: GREEN }} data-testid="comparison-export-excel">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Excel
            </Button>
            <Button size="sm" onClick={() => doExport('pdf')} disabled={exporting || selectedIds.length === 0} style={{ background: RUST }} data-testid="comparison-export-pdf">
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
            </Button>
          </div>
        </div>

        {/* Product chips */}
        <div className="mt-4">
          <p className="text-xs mb-2" style={{ color: MUTED }}>
            Pick products to compare ({selectedIds.length}/{MAX_SELECT} selected)
          </p>
          {summaries.length === 0 ? (
            <p className="text-sm py-2" style={{ color: MUTED }}>No products with a saved formulation yet. Build recipes in the Formulations tab first.</p>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="comparison-product-list">
              {summaries.map((s) => {
                const on = selectedIds.includes(s.product_id);
                const disabled = !on && selectedIds.length >= MAX_SELECT;
                return (
                  <button
                    key={s.product_id}
                    onClick={() => toggle(s.product_id)}
                    disabled={disabled}
                    data-testid={`comparison-chip-${s.product_id}`}
                    className="px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
                    style={on
                      ? { background: GREEN, color: 'white', borderColor: GREEN }
                      : { background: 'white', color: disabled ? '#B9BEB6' : '#1E2420', borderColor: '#E2DFD8', cursor: disabled ? 'not-allowed' : 'pointer' }}
                  >
                    {s.product_name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Card>

      {selectedSummaries.length === 0 ? (
        <Card className="p-10 text-center">
          <BarChart3 className="w-8 h-8 mx-auto mb-2" style={{ color: '#B9BEB6' }} />
          <p className="text-sm" style={{ color: MUTED }}>Select one or more products above to see the comparison charts.</p>
        </Card>
      ) : (
        <>
          {/* Cost comparison */}
          <Card className="p-4" data-testid="comparison-cost-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: GREEN }}>
                <BarChart3 className="w-4 h-4" /> Cost Comparison
              </h3>
              <div className="flex gap-1">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMetric(m.key)}
                    data-testid={`comparison-metric-${m.key}`}
                    className="px-2.5 py-1 rounded text-xs font-medium border transition-colors"
                    style={metric === m.key ? { background: GREEN, color: 'white', borderColor: GREEN } : { background: 'white', color: MUTED, borderColor: '#E2DFD8' }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={costChartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDEAE3" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: MUTED }} interval={0} angle={-12} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: MUTED }} />
                <Tooltip
                  formatter={(v) => [valueFmt(v), metricMeta.label]}
                  labelFormatter={(l, p) => (p && p[0] && p[0].payload.fullName) || l}
                />
                <Bar dataKey="value" name={metricMeta.label} radius={[4, 4, 0, 0]}>
                  {costChartData.map((d, i) => (
                    <Cell key={i} fill={d.value < 0 ? RUST : GREEN} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Full metric table */}
            <div className="overflow-x-auto mt-4">
              <table className="w-full text-xs" data-testid="comparison-cost-table">
                <thead>
                  <tr style={{ background: '#FBF9F6' }}>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-right">Cost/kg</th>
                    <th className="p-2 text-right">Cost/bag</th>
                    <th className="p-2 text-right">Sell/bag</th>
                    <th className="p-2 text-right">Margin ₹</th>
                    <th className="p-2 text-right">Margin %</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSummaries.map((s) => (
                    <tr key={s.product_id} className="border-t" style={{ borderColor: '#E2DFD8' }}>
                      <td className="p-2">{s.product_name}</td>
                      <td className="p-2 text-right">₹{fmt2(s.cost_per_kg)}</td>
                      <td className="p-2 text-right">₹{fmt2(s.cost_per_bag)}</td>
                      <td className="p-2 text-right">₹{fmt2(s.selling_price)}</td>
                      <td className="p-2 text-right" style={{ color: (s.margin_abs < 0) ? RUST : '#1E2420' }}>₹{fmt2(s.margin_abs)}</td>
                      <td className="p-2 text-right" style={{ color: (s.margin_pct != null && s.margin_pct < 0) ? RUST : '#1E2420' }}>
                        {s.margin_pct != null ? `${fmt2(s.margin_pct)}%` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* RM mix */}
          <Card className="p-4" data-testid="comparison-rm-card">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <h3 className="font-semibold flex items-center gap-2" style={{ color: GREEN }}>
                <Layers className="w-4 h-4" /> Raw-material Mix
              </h3>
              <div className="flex gap-1">
                <button
                  onClick={() => setRmMode('cost')}
                  data-testid="comparison-rmmode-cost"
                  className="px-2.5 py-1 rounded text-xs font-medium border transition-colors"
                  style={rmMode === 'cost' ? { background: GREEN, color: 'white', borderColor: GREEN } : { background: 'white', color: MUTED, borderColor: '#E2DFD8' }}
                >Cost (₹/kg)</button>
                <button
                  onClick={() => setRmMode('percent')}
                  data-testid="comparison-rmmode-percent"
                  className="px-2.5 py-1 rounded text-xs font-medium border transition-colors"
                  style={rmMode === 'percent' ? { background: GREEN, color: 'white', borderColor: GREEN } : { background: 'white', color: MUTED, borderColor: '#E2DFD8' }}
                >% of recipe</button>
              </div>
            </div>
            <p className="text-xs mb-2" style={{ color: MUTED }}>
              {rmMode === 'cost'
                ? 'Each cell = that raw material\u2019s cost per kg of finished product (\u20B9/kg).'
                : 'Each cell = that raw material\u2019s share of the recipe (%).'}
            </p>
            <div className="overflow-x-auto" data-testid="comparison-rm-table-wrap">
              <table className="w-full text-xs border-collapse" data-testid="comparison-rm-table">
                <thead>
                  <tr style={{ background: GREEN, color: 'white' }}>
                    <th className="p-2 text-left sticky left-0 z-10" style={{ background: GREEN, minWidth: 180 }}>Raw Material</th>
                    {selectedSummaries.map((s) => (
                      <th key={s.product_id} className="p-2 text-right whitespace-nowrap" title={s.product_name} style={{ minWidth: 110 }}>
                        {shortName(s.product_name)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rmKeys.length === 0 && (
                    <tr><td colSpan={selectedSummaries.length + 1} className="p-4 text-center" style={{ color: MUTED }}>No raw materials in the selected recipes.</td></tr>
                  )}
                  {rmKeys.map((rm, ri) => (
                    <tr key={rm} className="border-t" style={{ borderColor: '#E2DFD8', background: ri % 2 ? '#FBF9F6' : 'white' }} data-testid={`comparison-rm-row-${ri}`}>
                      <td className="p-2 font-medium sticky left-0 z-10" style={{ background: ri % 2 ? '#FBF9F6' : 'white', color: '#1E2420' }}>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: RM_COLORS[ri % RM_COLORS.length] }} />
                          {rm}
                        </span>
                      </td>
                      {rmChartData.map((row, ci) => {
                        const v = Number(row[rm] || 0);
                        return (
                          <td key={ci} className="p-2 text-right" style={{ color: v ? '#1E2420' : '#B9BEB6' }}>
                            {v ? (rmMode === 'cost' ? `\u20B9${fmt2(v)}` : `${fmt2(v)}%`) : '\u2014'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {rmKeys.length > 0 && (
                  <tfoot>
                    <tr style={{ background: '#2B5339', color: 'white', fontWeight: 700 }}>
                      <td className="p-2 sticky left-0 z-10" style={{ background: '#2B5339' }}>
                        {rmMode === 'cost' ? 'Total RM cost / kg' : 'Total %'}
                      </td>
                      {rmChartData.map((row, ci) => {
                        const tot = rmKeys.reduce((a, k) => a + Number(row[k] || 0), 0);
                        return (
                          <td key={ci} className="p-2 text-right">
                            {rmMode === 'cost' ? `\u20B9${fmt2(tot)}` : `${fmt2(tot)}%`}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
